import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const useVisibilityNotification = () => {
  const [lastInteractionTime, setLastInteractionTime] = useState(Date.now());
  const [lastNotificationTime, setLastNotificationTime] = useState(Date.now());
  const location = useLocation();

  const isExcludedPage =
    location.pathname === '/thankyou' || location.pathname === '/thankyou-bl';

  useEffect(() => {
    if (isExcludedPage) {
      localStorage.setItem('formCompleted', 'true');
      return;
    }

    let notificationTimeout;
    let inactivityTimeout;

    // Register Service Worker
    const registerSW = async () => {
      if ('serviceWorker' in navigator) {
        try {
          await navigator.serviceWorker.register('/sw.js');
        } catch (err) {
          console.error('Service worker registration failed:', err);
        }
      }
    };

    const requestPermission = async () => {
      if (!('Notification' in window)) return false;
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    };

    const showNotification = async () => {
      // 🚫 only if page is hidden
      if (!document.hidden) return;

      const now = Date.now();
      if (
        localStorage.getItem('formCompleted') === 'true' ||
        now - lastInteractionTime >= 120000 || // too long inactive
        now - lastNotificationTime < 120000    // too soon since last
      ) {
        return;
      }

      setLastNotificationTime(now);

      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          reg.showNotification('Special Offers Waiting!', {
            body: 'Would you like to see our exclusive loan offers? Complete your application now!',
            icon: '/noti.png',
            tag: 'loan-offer',
            requireInteraction: true
          });
        }
      }
    };

    const resetInactivityTimer = () => {
      if (inactivityTimeout) clearTimeout(inactivityTimeout);
      inactivityTimeout = setTimeout(showNotification, 60000); // 1 min
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // start timer only if hidden
        if (inactivityTimeout) clearTimeout(inactivityTimeout);
        notificationTimeout = setTimeout(showNotification, 60000);
      } else {
        // clear timers when visible
        if (notificationTimeout) clearTimeout(notificationTimeout);
        if (inactivityTimeout) clearTimeout(inactivityTimeout);
      }
    };

    const handleUserInteraction = () => {
      setLastInteractionTime(Date.now());
      resetInactivityTimer();
    };

    (async () => {
      await registerSW();
      const granted = await requestPermission();
      if (granted) resetInactivityTimer();
    })();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('mousemove', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);
    document.addEventListener('click', handleUserInteraction);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('mousemove', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('click', handleUserInteraction);
      if (notificationTimeout) clearTimeout(notificationTimeout);
      if (inactivityTimeout) clearTimeout(inactivityTimeout);
    };
  }, [isExcludedPage, lastInteractionTime, location.pathname]);

  return null;
};

export default useVisibilityNotification;
